import { useMemo } from 'react';
import { useRestQuery } from 'utils/hooks/api';

import { useMappingIdCache } from './useMappingIdCache';
import { DEFAULT_RESULTS } from './constants';
import {
  UseMappingsHookOutput,
  MappingsQueryData,
  UseMappingsHookProps,
  MappingMetadata,
} from './types';
import { UNDERSCORE_SUBSCRIBED_MESSAGE_TYPE_IDS } from './constants';
import { useCatalogFieldMappings } from './useCatalogFieldMappings';
import { useQueryStateContext } from '../../../contexts';
import { getMappingsRequestParams } from '../../../utils';
import { ESIndex } from '../../../types';

/**
 * A hook that returns mapping meta data.
 * If esIndex is Catalog it will always return catalog field mappings.
 * If esIndex is UserAndEvent it will return the field mappings based on the dataType provided.
 * */
export const useMappings = ({
  dataType,
  isEnabled = true,
}: UseMappingsHookProps): UseMappingsHookOutput => {
  const { esIndex } = useQueryStateContext();
  const { getIdForMapping } = useMappingIdCache();

  const { endpoint, params, config } = getMappingsRequestParams({
    dataType,
    isEnabled: isEnabled && esIndex !== ESIndex.Catalog,
  });
  const {
    data: results = DEFAULT_RESULTS,
    isLoading: isEventFieldMappingsLoading,
    isError: didEventFieldMappingsError,
  } = useRestQuery<MappingsQueryData>(endpoint, params, config);

  const {
    mappings: catalogFieldMappings,
    isLoading: isCatalogFieldMappingsLoading,
    didError: didCatalogFieldMappingsError,
  } = useCatalogFieldMappings();

  const eventFieldMappings: MappingMetadata[] = useMemo(
    () =>
      results
        .filter(({ fieldName }) => fieldName !== UNDERSCORE_SUBSCRIBED_MESSAGE_TYPE_IDS)
        .map(({ fieldName, dataType: mappingDataType, fieldType }) => ({
          id: getIdForMapping(mappingDataType, fieldName),
          name: fieldName,
          fieldType,
          dataType: mappingDataType,
        })),
    [results, getIdForMapping],
  );

  const mappings = useMemo(
    () => (esIndex === ESIndex.Catalog ? catalogFieldMappings : eventFieldMappings),
    [catalogFieldMappings, eventFieldMappings],
  );
  const isLoading = useMemo(
    () => (ESIndex.Catalog ? isCatalogFieldMappingsLoading : isEventFieldMappingsLoading),
    [isCatalogFieldMappingsLoading, isEventFieldMappingsLoading],
  );
  const didError = useMemo(
    () => (ESIndex.Catalog ? didCatalogFieldMappingsError : didEventFieldMappingsError),
    [didCatalogFieldMappingsError, didEventFieldMappingsError],
  );

  return {
    mappings,
    isLoading,
    didError,
  };
};
